'use client';
import Image from '@repo/ui/src/components/Image';
import createCurrencyString from '@functions/currency-functions';
import useCurrentUrl from '@repo/ui/src/hooks/useCurrentUrl';
import useTranslation from '@repo/ui/src/context/Translation/useTranslation';
import { Checkbox } from '@components/Form';
import Product from '@models/product';
import useBasket from '@hooks/basket/useBasket';
import { productToAnalytics } from '@repo/ui/src/services/analytics/factories';
import { productClicked, ProductClicked } from '@repo/ui/src/services/analytics/segment';
import { ModalID, useModal } from '@repo/ui/src/modules/UI';
import Button, { ButtonVariant } from '@repo/ui/src/components/Button';

interface ListLineItemProps {
  product: Product;
  disabled: boolean;
}

const ListLineItem = ({ product, disabled }: ListLineItemProps) => {
  const { locale } = useCurrentUrl();
  const { data: basket } = useBasket();
  const { t } = useTranslation();
  const { push } = useModal(ModalID.learnMore);
  const outOfStock = t('productDetailPageContent.outOfStockText', undefined, {
    default: 'Out of Stock',
  });

  return (
    <li className="tw-grid tw-grid-cols-[auto_auto_1fr_auto] tw-w-full tw-gap-4 tw-p-4 tw-items-center tw-bg-warm-100">
      <Checkbox
        key={product?.sku}
        id={product?.sku}
        name={product?.sku}
        disabled={!product.isOrderable || disabled}
        testId={`checkbox-${product?.sku}`}
        labelStyle="tra-text-web-body-m"
      />

      <div className="tw-size-16">
        {product?.mainImageUrl && (
          <Image
            src={product?.mainImageUrl}
            alt={product.title}
            layout="responsive"
            height={200}
            width={200}
          />
        )}
      </div>
      <div>
        <Button
          className="tra-text-web-sub-3 hover:tw-text-brand-300 tw-underline hover:tw-underline tw-self-center tw-cursor-pointer tw-normal-case"
          data-testid="shopable-recipe-link"
          variant={ButtonVariant.bare}
          size="none"
          onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            push(product);
            const clickProps = {
              ...productToAnalytics(product, locale),
              list_name: 'shopable recipes',
            } as ProductClicked;

            productClicked(clickProps);
          }}
        >
          {product?.title}
        </Button>
      </div>
      {product?.isOrderable ? (
        <div className="tw-flex tw-flex-col">
          {product.hasDiscount && (
            <p className="tra-text-web-body-m tw-mt-2 tw-mb-0 tw-line-through tw-pr-2 tw-text-neutral-400">
              {createCurrencyString(locale, basket?.currency ?? 'USD', product?.originalPrice ?? 0)}
            </p>
          )}
          <p className="tra-text-web-body-m tw-mb-0">
            {createCurrencyString(locale, basket?.currency ?? 'USD', product?.price)}
          </p>
        </div>
      ) : (
        <div className="tw-bg-neutral-300 tw-py-1 tw-px-4" data-testid="shopable-out">
          <p className="tra-text-web-utl-link tw-mb-0 tw-text-white">{outOfStock}</p>
        </div>
      )}
    </li>
  );
};

export default ListLineItem;
